import classNames from 'classnames';
import isArray from 'lodash/isArray';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import ConsentButton from '../buttons/Consent';

import styles from '../../../styles/menus/menu.module.css';

interface MenuItem {
    id?: string | null;
    key?: string | null;
    label?: ReactNode;
    ariaLabel?: string | null;
    url?: string | null;
    external?: boolean;
    spacer?: boolean;
    current?: boolean;
    withoutStyle?: boolean;
}

interface MenuProps {
    items: (MenuItem | MenuItem[])[];
    onClickItem?: () => void;
    className?: string | null;
    subItemsClassName?: string | null;
    itemsClassName?: string | null;
    itemClassName?: string | null;
    currentClassName?: string | null;
    spacerClassName?: string | null;
    linkClassName?: string | null;
}

function Menu({
    items,
    onClickItem = null,
    className = null,
    subItemsClassName = null,
    itemsClassName = null,
    itemClassName = null,
    currentClassName = null,
    spacerClassName = null,
    linkClassName = null,
}: MenuProps) {
    return (
        <div
            className={classNames({
                [styles.container]: true,
                [className]: className !== null,
            })}
        >
            <ul
                className={classNames([
                    styles.items,
                    {
                        [itemsClassName]: itemsClassName !== null,
                    },
                ])}
            >
                {items
                    .filter((f) => f !== null)
                    .map((it, index) => {
                        const isSubmenu = isArray(it);
                        const {
                            id = null,
                            key = null,
                            label = null,
                            ariaLabel = null,
                            url = null,
                            external = false,
                            spacer = false,
                            current = false,
                            withoutStyle = false,
                        } = isSubmenu ? {} : it;

                        const itemKey = `col-${key || index}`;

                        const finalClassName = classNames([
                            styles.item,
                            {
                                [styles.current]: current,
                                [itemClassName]: itemClassName !== null,
                                [currentClassName]: currentClassName !== null && current,
                            },
                        ]);

                        if (id === 'cookies') {
                            return (
                                <li className={finalClassName} key={itemKey}>
                                    <ConsentButton withoutStyle={withoutStyle} />
                                </li>
                            );
                        }

                        if (id === 'powered_by') {
                            return (
                                <li className={finalClassName} key={itemKey}>
                                    <div className={styles.poweredBy}>
                                        <a
                                            href="https://www.culturelaval.ca"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.cultureLaval}
                                        >
                                            <span>
                                                <FormattedMessage
                                                    defaultMessage="Propulsé par"
                                                    description="Header message"
                                                />
                                            </span>
                                            &nbsp;
                                            <FormattedMessage
                                                defaultMessage="Culture Laval"
                                                description="Header message"
                                            />
                                        </a>
                                    </div>
                                </li>
                            );
                        }

                        if (isSubmenu) {
                            return (
                                <li className={finalClassName} key={itemKey}>
                                    <Menu itemsClassName={subItemsClassName} items={it} />
                                </li>
                            );
                        }

                        if (spacer) {
                            return (
                                <li
                                    className={classNames([
                                        finalClassName,
                                        styles.spacer,
                                        {
                                            [spacerClassName]: spacerClassName !== null,
                                        },
                                    ])}
                                    key={itemKey}
                                />
                            );
                        }
                        return (
                            <li className={finalClassName} key={itemKey}>
                                {external ? (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={ariaLabel ?? undefined}
                                        className={classNames([
                                            styles.link,
                                            {
                                                [linkClassName]: linkClassName !== null,
                                            },
                                        ])}
                                    >
                                        {label}
                                    </a>
                                ) : (
                                    <Link
                                        href={url}
                                        aria-label={ariaLabel ?? undefined}
                                        onClick={() => {
                                            if (window.scrollTo) {
                                                window.scrollTo({
                                                    top: 0,
                                                });
                                            }
                                            if (onClickItem) {
                                                onClickItem();
                                            }
                                        }}
                                        className={classNames([
                                            styles.link,
                                            {
                                                [linkClassName]: linkClassName !== null,
                                            },
                                        ])}
                                    >
                                        {label}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
            </ul>
        </div>
    );
}

export default Menu;
